import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import { useNavigate } from "react-router-dom";
import { formatDateWithHour, formatDateWithoutHour } from "../../utils/date";
import { maskPhone, maskCurrency } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";
import config from "../../config";
import FormLabel from "../../components/FormLabel";
import usePermission from "../../hooks/usePermission";

const CadastrarEdital = ({ editalId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(editalId);
    const podeDeletar = usePermission("DELETAR_LICITACAO");

    // Estados dos modais de confirmação/sucesso
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showConfirmRemoveArchiveDialog, setShowConfirmRemoveArchiveDialog] = useState(false);

    const [categorias, setCategorias] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [orgaosEmissores, setOrgaosEmissores] = useState([]);
    const [modalidades, setModalidades] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [classificacoes, setClassificacoes] = useState([]);
    const [obras, setObras] = useState([]);

    // Array de "documentos vinculados" ao Edital (tabela dinâmica)
    const [documentosSelecionados, setDocumentosSelecionados] = useState([]);
    const [documentosRemovidos, setDocumentosRemovidos] = useState([]);

    // Anexos escolhidos em "Anexos e Documentos Complementares"
    const [anexosSelecionados, setAnexosSelecionados] = useState([]);
    const [anexosRemovidos, setAnexosRemovidos] = useState([]);

    const [projetosSelecionados, setProjetosSelecionados] = useState([]);
    const [projetosRemovidos, setProjetosRemovidos] = useState([]);

    // Campo auxiliar para armazenar o arquivo selecionado para anexo
    const [anexoFile, setAnexoFile] = useState(null);

    const [listAnexoFile, setListAnexoFile] = useState([]);

    const [showDialog, setShowDialog] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState("");




    // Estado principal do formulário
    const [formData, setFormData] = useState({
        numeroEdital: "",
        objeto: "",
        categoriaId: "",
        orgaoEmissorId: "",
        modalidadeId: "",
        statusId: "",
        classificacaoId: "",
        dataSessaoPublica: "",
        localidade: "",
        valorEstimado: "",
        descontoPercentual: "",
        percentualBdi: "",
        valorComDesconto: "",
        responsavelContato: "",
        cargoContato: "",
        orgaoContato: "",
        emailContato: "",
        urlContato: "",
        observacoes: "",
        // Caso tenha um "baseDocumentos", "tipoDocumentoProjeto", etc., adicione abaixo:
        baseDocumentos: "",
        tipoDocumentoProjeto: "",
        tipoDocumentoAnexo: "",

        // Upload de arquivo
        arquivo: null,
        arquivoUrl: "",
    });

    // Caso tenha validações, poderia usar useValidation
    // const { errors, handleChange, validateForm } = useValidation(formData, { ... });

    // Carregar dados do edital (edição)
    useEffect(() => {

        // Carregar categorias e status da API
        const fetchData = async () => {
            try {
                const [categoriasRes, statusRes, classificacoesRes, tiposDocumentosRes, modalidadesRes, orgaosEmissoresRes, documentosRes, obrasRes] = await Promise.all([
                    api.get("/categorias/byModule/nome/editais"),
                    api.get("/statuses/byModule/nome/editais"),
                    api.get("/classificacoes"),
                    api.get("/tiposdocumentos"),
                    api.get("/modalidades"),
                    api.get("/orgaosemissores"),
                    api.get("/documentos/todos"),
                    api.get("/obras/listar/todas"),

                ]);
                setCategorias(categoriasRes.data);
                setStatusList(statusRes.data);
                setClassificacoes(classificacoesRes.data);
                setTiposDocumentos(tiposDocumentosRes.data);
                setModalidades(modalidadesRes.data);
                setOrgaosEmissores(orgaosEmissoresRes.data);
                setDocumentos(documentosRes.data);
                setObras(obrasRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();

        if (isEditMode) {
            const fetchEdital = async () => {
                try {
                    const response = await api.get(`/editais/${editalId}`);
                    const data = response.data;
                    setFormData({
                        numeroEdital: data.numeroEdital || "",
                        objeto: data.objeto || "",
                        categoriaId: data.categoriaId?.toString() || "",
                        orgaoEmissorId: data.orgaoEmissorId?.toString() || "",
                        modalidadeId: data.modalidadeId?.toString() || "",
                        statusId: data.statusId?.toString() || "",
                        classificacaoId: data.classificacaoId?.toString() || "",
                        dataSessaoPublica: data.dataSessaoPublica
                            ? data.dataSessaoPublica.split("T")[0]
                            : "",
                        localidade: data.localidade || "",
                        valorEstimado: maskCurrency(Math.round(data.valorEstimado * 100)) ?? "",
                        percentualBdi: data.bdiPercentual ?? "",
                        descontoPercentual: data.descontoPercentual ?? "",
                        valorComDesconto: maskCurrency(Math.round(data.valorComDesconto * 100)) ?? "",
                        responsavelContato: data.responsavelContato || "",
                        cargoContato: data.cargoContato || "",
                        orgaoContato: maskPhone(data.orgaoContato) || "",
                        emailContato: data.emailContato || "",
                        urlContato: data.urlContato || "",
                        observacoes: data.observacoes || "",
                        // Se o backend retornar estes campos, inclua também:
                        baseDocumentos: "",
                        tipoDocumentoProjeto: "",
                        tipoDocumentoAnexo: "",
                        arquivo: null,
                        arquivoUrl: data.arquivoUrl || "",
                    });
                    setAnexosSelecionados(data.anexos || []);
                    setAnexosRemovidos([]);

                    setDocumentosSelecionados(data.documentos || []);
                    setDocumentosRemovidos([]);

                    setProjetosSelecionados(data.obrasEdital || []);
                    setProjetosRemovidos([]);
                } catch (error) {
                    console.error("Erro ao carregar edital:", error);
                    alert("Erro ao carregar o edital. Tente novamente.");
                }
            };
            fetchEdital();
        }
    }, [editalId, isEditMode]);

    // Handler genérico para inputs e selects
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handler específico para campo de telefone
    const handlePhoneChange = (e) => {
        const originalValue = e.target.value;
        const masked = maskPhone(originalValue);
        setFormData((prev) => ({ ...prev, orgaoContato: masked }));
    };

    // Handler específico para campo de moeda
    const handleCurrencyChange = (fieldName) => (e) => {
        const originalValue = e.target.value;
        const masked = maskCurrency(originalValue);
        if (fieldName === 'valorEstimado') {
            setFormData((prev) => ({ ...prev, ['descontoPercentual']: '' }));
            setFormData((prev) => ({ ...prev, ['valorComDesconto']: '' }));
        }
        setFormData((prev) => ({ ...prev, [fieldName]: masked }));
    };

    const handleDescontoPercentualChange = (e) => {
        const digitado = e.target.value; // ex. "10", "15%", ...

        setFormData((prev) => {
            const updated = { ...prev, descontoPercentual: digitado };

            // Precisamos verificar se Valor da Licitação já está preenchido
            const licFloat = parseCurrencyToFloat(prev.valorEstimado);

            // Se licFloat == 0, limpamos o valorComDesconto e saímos
            if (licFloat === 0) {
                updated.valorComDesconto = '';
                return updated;
            }

            // Converter "digitado" em float -> ex. "10" => 10.0 => 0.10
            let descFloat = 0;
            const apenasNumero = digitado.replace('%', '').trim();
            descFloat = parseFloat(apenasNumero);
            if (descFloat > 100) {
                updated.valorComDesconto = '';
                return updated
            }
            if (isNaN(descFloat)) descFloat = 0;

            if (licFloat > 0 && descFloat > 0) {
                const percentAplicar = descFloat / 100;
                const percentValor = licFloat * percentAplicar;
                const valor = licFloat - percentValor;
                updated.valorComDesconto = valor.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                });

            } else {
                // Se não tem desconto válido, limpamos
                updated.valorComDesconto = '';
            }

            return updated;
        });
    };

    // Handler para arquivos
    const handleFileChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            arquivo: e.target.files[0],
        }));
    };

    // Para o anexoFile de "Anexos e Documentos Complementares"
    const handleAnexoFileChange = (e) => {
        setAnexoFile(e.target.files[0]);
    };

    const handleAddAnexo = () => {
        if (!formData.tipoDocumentoAnexo) {
            alert("Selecione um Tipo de Documento antes de enviar.");
            return;
        }
        if (!anexoFile) {
            alert("Selecione um arquivo antes de enviar.");
            return;
        }

        // Encontra no array "tiposDocumentos" o objeto do tipo selecionado
        const tipoId = parseInt(formData.tipoDocumentoAnexo, 10);
        const tipoEncontrado = tiposDocumentos.find((t) => t.id === tipoId);
        if (!tipoEncontrado) {
            alert("Tipo de documento não encontrado.");
            return;
        }

        // Cria objeto com tipo, file, data de envio, etc.
        const novoAnexo = {
            id: Date.now(), // ID local, único
            tipo: tipoId,
            tipoNome: tipoEncontrado.nome,
            file: anexoFile,
            dataEnvio: new Date().toISOString(), // ou formate como quiser
            isNew: true,
        };
        setAnexosSelecionados((prev) => [...prev, novoAnexo]);
        setListAnexoFile((prev) => [...prev, anexoFile])



        // Opcional: Resetar "Pesquisar Documento"
        setAnexoFile(null);
    };

    const handleRemoveAnexo = (anexoId) => {
        setAnexosSelecionados((prevSelecionados) => {
            // 1) Localiza o anexo
            const item = prevSelecionados.find((a) => a.id === anexoId);
            if (!item) return prevSelecionados; // se não achar, não faz nada

            // 2) Se é novo (isNew === true) ou ID temporário (p. ex.: 'temp-...'),
            //    basta remover do array principal, pois não precisa excluir no backend.
            if (item.isNew || String(item.id).startsWith('temp-')) {
                return prevSelecionados.filter((a) => a.id !== anexoId);
            } else {
                // 3) Caso contrário, movemos para anexosRemovidos,
                //    MAS evitamos duplicar caso já esteja lá.
                setAnexosRemovidos((prevRemovidos) => {
                    const jaExiste = prevRemovidos.some((r) => r.id === item.id);
                    if (!jaExiste) {
                        return [...prevRemovidos, item];
                    }
                    return prevRemovidos;
                });

                // 4) E removemos do array principal
                return prevSelecionados.filter((a) => a.id !== anexoId);
            }
        });
    };

    // Submeter formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        // if (!validateForm(formData)) return;
        setShowConfirmDialog(true);
    };

    // Confirmar (POST ou PUT)
    const handleConfirm = async () => {
        setShowConfirmDialog(false);

        try {
            const dataToSend = new FormData();
            // Adiciona todos os campos do formData no dataToSend
            // (exceto "arquivoUrl", pois é apenas informativo)
            dataToSend.append("numeroEdital", formData.numeroEdital);
            dataToSend.append("objeto", formData.objeto);
            dataToSend.append("categoriaId", formData.categoriaId);
            dataToSend.append("orgaoEmissorId", formData.orgaoEmissorId);
            dataToSend.append("modalidadeId", formData.modalidadeId);
            dataToSend.append("statusId", formData.statusId);
            dataToSend.append("classificacaoId", formData.classificacaoId);
            dataToSend.append("dataSessaoPublica", formData.dataSessaoPublica);
            dataToSend.append("localidade", formData.localidade);
            dataToSend.append("valorEstimado", parseCurrencyToFloat(formData.valorEstimado));
            dataToSend.append("percentualBdi", formData.percentualBdi);
            dataToSend.append("descontoPercentual", formData.descontoPercentual);
            dataToSend.append("valorComDesconto", parseCurrencyToFloat(formData.valorComDesconto));
            dataToSend.append("responsavelContato", formData.responsavelContato);
            dataToSend.append("cargoContato", formData.cargoContato);
            dataToSend.append("orgaoContato", formData.orgaoContato);
            dataToSend.append("emailContato", formData.emailContato);
            dataToSend.append("urlContato", formData.urlContato);
            dataToSend.append("observacoes", formData.observacoes);
            dataToSend.append("baseDocumentos", formData.baseDocumentos);
            dataToSend.append("tipoDocumentoProjeto", formData.tipoDocumentoProjeto);
            dataToSend.append("tipoDocumentoAnexo", formData.tipoDocumentoAnexo);
            dataToSend.append("documentosAdicionados", JSON.stringify(documentosSelecionados));
            dataToSend.append("documentosRemovidos", JSON.stringify(documentosRemovidos));
            dataToSend.append("anexosAdicionados", JSON.stringify(anexosSelecionados));
            dataToSend.append("anexosRemovidos", JSON.stringify(anexosRemovidos));
            dataToSend.append("projetosAdicionados", JSON.stringify(projetosSelecionados));
            dataToSend.append("projetosRemovidos", JSON.stringify(projetosRemovidos));

            anexosSelecionados.forEach((anexo, index) => {
                dataToSend.append(`anexosFiles[${index}]`, anexo.file);
            });

            // Se tiver arquivo para upload
            if (formData.arquivo) {
                dataToSend.append("arquivo", formData.arquivo);
            }

            if (isEditMode) {
                await api.put(`/editais/${editalId}`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                await api.post(`/editais`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                if (isEditMode) {
                    window.location.href = `/editais/editar/${editalId}`;
                } else {
                    window.location.href = `/editais`;
                }
            }, 3000);
        } catch (error) {
            console.error(`Erro ao ${isEditMode ? "atualizar" : "cadastrar"} edital:`, error);
            alert("Ocorreu um erro. Verifique os dados e tente novamente.");
        }
    };

    // Cancelar modal
    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    // Deletar registro
    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/editais/${editalId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/editais");
        } catch (error) {
            console.error("Erro ao remover edital:", error);
            alert("Erro ao remover edital.");
        }
    };

    // Voltar
    const handleBack = () => {
        navigate("/editais");
    };

    // Remover arquivo
    const handleDeleteFile = async () => {
        try {
            await api.delete(`/editais/${editalId}/arquivo`);
            setFormData((prev) => ({ ...prev, arquivoUrl: "" }));
            setShowConfirmRemoveArchiveDialog(false);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao remover arquivo:", error);
            alert("Erro ao remover arquivo.");
        }
    };

    // Adicionar Documento à tabela
    const handleAddDocumento = () => {
        if (!formData.baseDocumentos) {
            alert("Selecione um documento antes de adicionar.");
            return;
        }

        // Encontrar no array todosDocumentos o objeto cujo ID === formData.baseDocumentos
        const docSelecionado = documentos.find(
            (doc) => doc.id === parseInt(formData.baseDocumentos, 10)
        );
        if (!docSelecionado) {
            alert("Documento não encontrado na lista.");
            return;
        }

        // Exemplo: extrair campos do docSelecionado que deseja exibir
        const novoDoc = {
            id: docSelecionado.id,
            nomeDocumento: docSelecionado.nomeDocumento,
            validade: docSelecionado.validade,
            dataEnvio: docSelecionado.dataUpload, // ou outro campo
            isNew: true,
        };

        // Adicionar ao array
        setDocumentosSelecionados((prev) => [...prev, novoDoc]);
    };

    // Remover documento da tabela
    const handleRemoveDocumento = (docId) => {
        setDocumentosSelecionados((prevSelecionados) => {
            // 1) Localiza o item
            const item = prevSelecionados.find((d) => d.id === docId);
            if (!item) return prevSelecionados; // se não achou, não faz nada

            // 2) Se é novo (isNew) ou ID temporário, podemos simplesmente remover do array
            if (item.isNew || String(item.id).startsWith('temp-')) {
                return prevSelecionados.filter((d) => d.id !== docId);
            } else {
                // 3) Se não é novo, precisamos movê-lo p/ documentosRemovidos
                //    MAS garantir que não foi adicionado antes
                setDocumentosRemovidos((prevRemovidos) => {
                    const jaExiste = prevRemovidos.some((r) => r.id === item.id);
                    if (!jaExiste) {
                        return [...prevRemovidos, item];
                    }
                    return prevRemovidos;
                });

                // 4) E removemos do array principal
                return prevSelecionados.filter((d) => d.id !== docId);
            }
        });
    };

    const handleAdicionarProjeto = () => {
        // Validar se o usuário realmente selecionou algo
        if (!formData.tipoDocumentoProjeto) {
            alert("Selecione um projeto antes de adicionar.");
            return;
        }

        // Encontrar no array `obras` o objeto cujo ID === formData.tipoDocumentoProjeto
        const obraSelecionada = obras.find(
            (o) => o.id === parseInt(formData.tipoDocumentoProjeto, 10)
        );
        if (!obraSelecionada) {
            alert("Não encontrado na lista de obras.");
            return;
        }

        // Montar objeto do "projeto vinculado"
        const novoProjeto = {
            id: obraSelecionada.id,
            nomeProjeto: obraSelecionada.nomeProjeto,
            dataVinculo: new Date().toISOString(), // ou outro critério
            isNew: true,  // Indica que ainda não existe no backend
        };

        // Adiciona ao array de projetosSelecionados
        setProjetosSelecionados((prev) => [...prev, novoProjeto]);

        // Se quiser, limpa o select
        setFormData((prev) => ({ ...prev, tipoDocumentoProjeto: "" }));
    };

    const handleRemoveProjeto = (projetoId) => {
        setProjetosSelecionados((prevSelecionados) => {
            // 1) Achar o item
            const item = prevSelecionados.find((p) => p.id === projetoId);
            if (!item) return prevSelecionados; // se não achar, não faz nada

            // 2) Se for novo (id “temp-xxx” ou `isNew`), basta excluir do array
            if (item.isNew || String(item.id).startsWith("temp-")) {
                return prevSelecionados.filter((p) => p.id !== projetoId);
            } else {
                // 3) Se o projeto já está no banco de dados,
                // precisamos movê-lo para projetosRemovidos
                setProjetosRemovidos((prevRemovidos) => {
                    const jaExiste = prevRemovidos.some((r) => r.id === item.id);
                    if (!jaExiste) {
                        return [...prevRemovidos, item];
                    }
                    return prevRemovidos;
                });

                // 4) E removemos do array principal
                return prevSelecionados.filter((p) => p.id !== projetoId);
            }
        });
    };

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Licitação /Edital" : "Cadastrar Licitação /Edital"}
                    </h1>
                </div>

                <div className="flex flex-col gap-4">
                    <h2 className="text-base text-stone-600 font-medium">Cadastrar Edital</h2>

                    {/* Informações Básicas */}
                    <div className="flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium font-['Inter']">
                            Informações Básicas
                        </div>
                        {/* Linha 1 */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                            {/* Número do Edital */}
                            <div className="md:w-[185px] w-full flex-col gap-1.5 inline-flex">
                                <FormLabel htmlFor={"numeroEdital"} isRequired={true}>
                                    Número do Edital
                                </FormLabel>
                                <input
                                    name="numeroEdital"
                                    type="text"
                                    value={formData.numeroEdital}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base font-normal"
                                    required
                                />
                            </div>

                            {/* Órgão Emissor */}
                            <div className="md:w-[374px] w-full flex-col justify-center gap-1.5 inline-flex">
                                <div className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Órgão Emissor
                                </div>
                                <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                    <select
                                        name="orgaoEmissorId"
                                        value={formData.orgaoEmissorId}
                                        onChange={handleInputChange}
                                        className="grow bg-transparent outline-none text-slate-700 text-base"
                                    >
                                        <option value="">Selecionar</option>
                                        {orgaosEmissores.map((objeto) => (
                                            <option key={objeto.id} value={objeto.id}>
                                                {objeto.nome}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                </div>
                            </div>

                            {/* Objeto do Edital */}
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <div className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Objeto do Edital
                                </div>
                                <input
                                    name="objeto"
                                    type="text"
                                    value={formData.objeto}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base font-normal"
                                />
                            </div>

                            {/* Tipo */}
                            <div className="md:w-[374px] w-full flex-col justify-center gap-1.5 inline-flex">
                                <div className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Tipo
                                </div>
                                <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                    <select
                                        name="categoriaId"
                                        value={formData.categoriaId}
                                        onChange={handleInputChange}
                                        className="grow bg-transparent outline-none text-slate-700 text-base"
                                    >
                                        <option value="">Selecionar</option>
                                        {categorias.map((objeto) => (
                                            <option key={objeto.id} value={objeto.id}>
                                                {objeto.nome}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                </div>
                            </div>
                        </div>

                        {/* Linha 2 */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                            {/* Modalidade */}
                            <div className="md:flex-1 w-full flex-col justify-center gap-1.5 inline-flex">
                                <div className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Modalidade
                                </div>
                                <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                    <select
                                        name="modalidadeId"
                                        value={formData.modalidadeId}
                                        onChange={handleInputChange}
                                        className="grow bg-transparent outline-none text-slate-700 text-base"
                                    >
                                        <option value="">Selecionar</option>
                                        {modalidades.map((objeto) => (
                                            <option key={objeto.id} value={objeto.id}>
                                                {objeto.nome}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                </div>
                            </div>
                            {/* Data da Sessão Pública */}
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <div className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Data da Sessão Pública
                                </div>
                                <input
                                    name="dataSessaoPublica"
                                    type="date"
                                    value={formData.dataSessaoPublica}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>
                            {/* Localidade (Muncípio) */}
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <div className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Localidade
                                </div>
                                <input
                                    name="localidade"
                                    type="text"
                                    value={formData.localidade}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>

                        </div>
                    </div>

                    {/* Valores e Orçamentos */}
                    <div className="flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium font-['Inter']">
                            Valores e Orçamentos
                        </div>

                        {/* Linha 1 */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Valor Estimado do Edital (R$)
                                </label>
                                <input
                                    name="valorEstimado"
                                    type="text"
                                    placeholder="R$ 00,00"
                                    value={formData.valorEstimado}
                                    onChange={handleCurrencyChange("valorEstimado")}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>

                        </div>

                        {/* Linha 2 */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Percentual do BDI (%)
                                </label>
                                <input
                                    name="percentualBdi"
                                    type="text"
                                    value={formData.percentualBdi}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>

                            <div className="w-[174px] flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Desconto (%)
                                </label>
                                <input
                                    name="descontoPercentual"
                                    type="text"
                                    value={formData.descontoPercentual}
                                    onChange={handleDescontoPercentualChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>

                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Valor com Desconto (R$)
                                </label>
                                <input
                                    name="valorComDesconto"
                                    type="text"
                                    value={formData.valorComDesconto}
                                    readOnly
                                    onChange={handleCurrencyChange("valorComDesconto")}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Documentação e Exigências */}
                    <div className="flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium font-['Inter']">
                            Documentação e Exigências
                        </div>

                        {/* Base de Documentos */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-end gap-4">
                            <div className="md:w-[654px] w-full flex-col justify-center gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                    Base de Documentos
                                </label>
                                <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                    <select
                                        name="baseDocumentos"
                                        value={formData.baseDocumentos}
                                        onChange={handleInputChange}
                                        className="grow bg-transparent outline-none text-slate-700 text-base"
                                    >
                                        <option value="">Selecionar</option>
                                        {documentos.map((objeto) => (
                                            <option key={objeto.id} value={objeto.id}>
                                                {objeto.nomeDocumento}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="w-4 h-4 px-1 flex justify-center items-center overflow-hidden" />
                                </div>
                            </div>
                            <button type="button" className="h-10 px-4 py-2 bg-stone-600 rounded-md inline-flex justify-center items-center gap-2" onClick={handleAddDocumento}>
                                <img className="w-4 h-4 overflow-hidden" src="/images/icon/plus-circle.png" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal">
                                    Adicionar
                                </div>
                            </button>
                        </div>

                        {/* Tabela de Documentos (conteúdo fixo) */}
                        <div className="w-full overflow-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            ID Documento
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Nome
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Validade
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Data do Envio
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Remover
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documentosSelecionados.map((doc) => (
                                        <tr key={doc.id}>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">
                                                {doc.id}
                                            </td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">
                                                {doc.nomeDocumento ?? doc.documento.nomeDocumento}
                                            </td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">
                                                {formatDateWithoutHour(doc.validade ?? doc.documento.validade) || "N/A"}
                                            </td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">
                                                {formatDateWithHour(doc.dataEnvio ?? doc.documento.dataUpload) || "N/A"}
                                            </td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveDocumento(doc.id)}
                                                    className="inline-flex items-center"
                                                >
                                                    <img
                                                        className="w-6 h-6 relative overflow-hidden"
                                                        src="/images/icon/x-circle-color.png"
                                                        alt="Remover"
                                                    />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {documentosSelecionados.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base text-center"
                                            >
                                                Nenhum documento adicionado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Informações de Contato e Comunicação */}
                    <div className="flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium font-['Inter']">
                            Informações de Contato e Comunicação
                        </div>
                        {/* Linha 1 */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                            {/* Responsável */}
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Responsável pelo Edital
                                </label>
                                <input
                                    name="responsavelContato"
                                    type="text"
                                    value={formData.responsavelContato}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>
                            {/* Cargo */}
                            <div className="md:w-[506.33px] w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Cargo
                                </label>
                                <input
                                    name="cargoContato"
                                    type="text"
                                    value={formData.cargoContato}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>
                        </div>
                        {/* Linha 2 */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                            {/* Contato do Órgão */}
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    Contato do Órgão
                                </label>
                                <input
                                    name="orgaoContato"
                                    type="text"
                                    value={formData.orgaoContato}
                                    onChange={handlePhoneChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>
                            {/* E-mail */}
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    E-mail
                                </label>
                                <input
                                    name="emailContato"
                                    type="email"
                                    value={formData.emailContato}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>
                            {/* URL de Contato */}
                            <div className="md:flex-1 w-full flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                    URL de Contato
                                </label>
                                <input
                                    name="urlContato"
                                    type="text"
                                    value={formData.urlContato}
                                    onChange={handleInputChange}
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Projetos Vinculados */}
                    <div className="flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium font-['Inter']">
                            Obras Vinculadas
                        </div>
                        {/* Botão Selecionar/Vincular */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-end gap-4">
                            <div className="md:w-[505px] w-full flex-col justify-center gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                    Obra
                                </label>
                                <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                    <select
                                        name="tipoDocumentoProjeto"
                                        value={formData.tipoDocumentoProjeto}
                                        onChange={handleInputChange}
                                        className="grow bg-transparent outline-none text-slate-700 text-base"
                                    >
                                        <option value="">Selecionar</option>
                                        {obras.map((objeto) => (
                                            <option key={objeto.id} value={objeto.id}>
                                                {objeto.nomeProjeto}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                </div>
                            </div>
                            <button type="button" className="px-4 py-2 bg-stone-600 rounded-md inline-flex justify-center items-center gap-2" onClick={handleAdicionarProjeto}>
                                <img className="w-4 h-4 overflow-hidden" src="/images/icon/plus-circle.png" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal">
                                    Vincular Obra
                                </div>
                            </button>
                        </div>
                        {/* Tabela de Projetos Vinculados (conteúdo fixo) */}
                        <div className="w-full overflow-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Nº
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Obra
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Data do Vinculo
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Remover
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projetosSelecionados.length > 0 ? (
                                        projetosSelecionados.map((proj, index) => (
                                            <tr key={proj.id}>
                                                {/* Nº ou Código, se o ID não for string, exibe algo simples */}
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">
                                                    {String(proj.id).startsWith("temp-") ? index + 1 : proj.id}
                                                </td>
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">
                                                    {proj?.obra?.nomeProjeto ?? proj.nomeProjeto}
                                                </td>
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-center text-slate-700 text-base">
                                                    {proj?.createdAt ? formatDateWithHour(proj?.createdAt) : formatDateWithHour(proj.dataVinculo)}
                                                </td>
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveProjeto(proj.id)}
                                                    >
                                                        <img
                                                            className="w-6 h-6 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden"
                                                            src="/images/icon/trash.png"
                                                            alt="Remover"
                                                        />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={4}
                                                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base text-center"
                                            >
                                                Nenhuma obra vinculada.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Anexos e Documentos Complementares */}
                    <div className="flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium font-['Inter']">
                            Anexos e Documentos Complementares
                        </div>
                        {/* Linha de Envio de Documento */}
                        <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                            <div className="md:w-[375.75px] w-full flex-col justify-center gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                    Tipo de Documento
                                </label>
                                <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                    <select
                                        name="tipoDocumentoAnexo"
                                        value={formData.tipoDocumentoAnexo}
                                        onChange={handleInputChange}
                                        className="grow bg-transparent outline-none text-slate-700 text-base"
                                    >
                                        <option value="">Selecionar</option>
                                        {tiposDocumentos.map((objeto) => (
                                            <option key={objeto.id} value={objeto.id}>
                                                {objeto.nome}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                </div>
                            </div>
                            <div className="grow flex-col gap-1.5 inline-flex">
                                <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                    Selecionar Documento
                                </label>
                                <div className="flex gap-2">
                                    <div className="relative flex items-center gap-4 w-full">
                                        <input
                                            name="arquivo"
                                            type="file"
                                            accept=".pdf"
                                            onChange={handleAnexoFileChange}
                                            className="absolute inset-0 opacity-0 cursor-pointer grow pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-400 text-base"

                                        />
                                        <div className={`w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2`} >
                                            <span className="text-base text-slate-400">
                                                {anexoFile ? anexoFile.name : "Pesquisar Documento"}
                                            </span>
                                        </div>
                                    </div>
                                    <button type="button" className="px-4 py-2 bg-stone-600 rounded-md flex justify-center items-center gap-2.5" onClick={handleAddAnexo}>
                                        <div className="text-white text-sm font-normal font-['Inter'] leading-normal">
                                            Enviar
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* Tabela de Anexos (conteúdo fixo) */}
                        <div className="w-full overflow-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Nº
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Tipo
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Data do Envio
                                        </th>
                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                            Remover
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {anexosSelecionados.length > 0 ? (
                                        anexosSelecionados.map((anexo) => (
                                            <tr key={anexo.id}>
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo.id}</td>
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo.tipoNome ?? anexo.tipo.nome}</td>
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{formatDateWithoutHour(anexo.dataEnvio)}</td>
                                                <td className="px-4 py-2 bg-white border border-slate-200 text-center">
                                                    <button type="button" onClick={() => handleShowPdf(anexo.arquivo)} title="Ver anexo">
                                                        <img className="w-10 h-10 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/search.png" />
                                                    </button>
                                                    <button type="button" onClick={() => handleRemoveAnexo(anexo.id)} title="Remover anexo">
                                                        <img className="w-6 h-6 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/trash.png" />
                                                    </button>

                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base text-center"
                                            >                                                Nenhum anexo enviado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Status e Observações / Atualizações */}
                    <div className="flex flex-col md:flex-row gap-8 w-full">
                        {/* Coluna 1 */}
                        <div className="md:w-1/2 w-full flex flex-col gap-4">
                            <div className="text-stone-600 text-base font-medium font-['Inter']">
                                Status e Observações
                            </div>
                            <div className="flex flex-col gap-4">
                                {/* Status / Classificação */}
                                <div className="flex flex-col md:flex-row flex-wrap gap-4">
                                    <div className="md:flex-1 w-full flex-col justify-center gap-1.5">
                                        <FormLabel htmlFor={"statusId"} isRequired={true}>
                                            Status
                                        </FormLabel>
                                        <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                            <select
                                                name="statusId"
                                                value={formData.statusId}
                                                onChange={handleInputChange}
                                                className="grow bg-transparent outline-none text-slate-700 text-base"
                                                required
                                            >
                                                <option value="">Selecionar</option>
                                                {statusList.map((objeto) => (
                                                    <option key={objeto.id} value={objeto.id}>
                                                        {objeto.nome}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                        </div>
                                    </div>
                                    <div className="md:flex-1 w-full flex-col justify-center gap-1.5">
                                        <FormLabel htmlFor={"classificacaoId"}>
                                            Classificação
                                        </FormLabel>
                                        <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                            <select
                                                name="classificacaoId"
                                                value={formData.classificacaoId}
                                                onChange={handleInputChange}
                                                className="grow bg-transparent outline-none text-slate-700 text-base"
                                            >
                                                <option value="">Selecionar</option>
                                                {classificacoes.map((objeto) => (
                                                    <option key={objeto.id} value={objeto.id}>
                                                        {objeto.nome}
                                                    </option>
                                                ))}

                                            </select>
                                            <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                        </div>
                                    </div>
                                </div>
                                {/* Observações */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-[14px]">
                                        Observações
                                    </label>
                                    <div className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 min-h-[100px] flex items-start">
                                        <textarea
                                            name="observacoes"
                                            value={formData.observacoes}
                                            onChange={handleInputChange}
                                            className="w-full bg-transparent outline-none text-slate-700 text-base"
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Coluna 2: Atualizações (fixo) */}
                        <div className="md:w-1/2 w-full flex flex-col items-start gap-2.5">
                            {/*<div className="flex flex-col gap-4 w-full">
                                <div className="text-stone-600 text-base font-medium font-['Inter']">
                                    Atualizações
                                </div>
                                <div className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                    ID: 1021 - João Silva atualizou documentos exigidos em 15/02/2024 14:30
                                    <br />
                                    ID: 1022 - Maria Fernandes alterou data de abertura em 25/03/2024 09:15
                                    <br />
                                    ID: 1023 - Pedro Almeida adicionou anexo do edital em 10/05/2024 16:45
                                </div>
                            </div>*/}
                        </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex items-center w-full gap-4">
                        {isEditMode && podeDeletar && (

                            <button
                                type="button"
                                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                                onClick={() => setShowConfirmDeleteRegister(true)}
                            >
                                <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Apagar" />
                                <span>Apagar</span>
                            </button>

                        )}

                        <div className="flex gap-4 ml-auto">
                            <button
                                type="button"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                                onClick={handleBack}
                            >
                                <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                                <span>Voltar</span>
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            >
                                <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                                <span>Salvar</span>
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            {/** MODAIS de Confirmação / Sucesso */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                message="Deseja salvar as informações?"
            />

            <SuccessModal
                isVisible={showSuccessDialog}
                message="Informações salvas com sucesso!"
                onClose={() => setShowSuccessDialog(false)}
            />

            <ConfirmationModal
                isVisible={showConfirmRemoveArchiveDialog}
                onConfirm={handleDeleteFile}
                onCancel={() => setShowConfirmRemoveArchiveDialog(false)}
                message="Deseja remover o arquivo deste edital?"
                textButtonConfirm="Sim"
            />

            <ConfirmationModal
                isVisible={showConfirmDeleteRegister}
                onConfirm={handleDeleteRegister}
                onCancel={() => setShowConfirmDeleteRegister(false)}
                message="Deseja apagar este registro?"
                textButtonConfirm="Sim"
            />

            {showDialog && (
                <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                    <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                        <div className="flex justify-between items-center p-4">
                            <div className="flex items-center gap-4">
                                <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                    <img className="w-6 h-6 relative  overflow-hidden" src="/images/icon/download.png" />
                                </a>
                                <div className="w-6 h-6 relative  overflow-hidden" />
                                <div className="w-6 h-6 relative  overflow-hidden" />
                            </div>
                            <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="/images/icon/x.png" onClick={handleCloseDialog} />
                        </div>
                        <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                            <iframe
                                src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                className="w-full h-full border-0"
                                style={{ border: 'none' }}
                                title="PDF Viewer"
                            />
                        </div>
                    </div>
                </div>
            )}
        </Template>
    );
};

export default CadastrarEdital;
